import { PROJECT_STATUS_MANUAL_HOLD } from './project-status';

export const ASSET_TYPE_LABELS: Record<string, string> = {
  WEB: 'Web',
  MOBILE: 'Mobile',
  NETWORK: 'Network',
  CLOUD: 'Cloud',
  SERVER: 'Server',
  API: 'API',
  THICK_CLIENT: 'Thick Client',
  SOURCE_CODE: 'Source Code',
  RED_TEAM: 'Red Team',
  ACTIVE_DIRECTORY: 'Active Directory',
};

export const VULNERABILITY_STATUS_LABELS: Record<string, string> = {
  OPEN: 'Open',
  MITIGATED: 'Mitigated',
  ACCEPTED_RISK: 'Accepted Risk',
  FALSE_POSITIVE: 'False Positive',
  FALSE_POSITIVE_VALID: 'False Positive (Valid)',
  RETESTING: 'Retesting',
};

export const ASSESSMENT_TYPE_LABELS: Record<string, string> = {
  BLACK_BOX: 'Black Box',
  GRAY_BOX: 'Gray Box',
  WHITE_BOX: 'White Box',
  OTHER: 'Other',
};

/**
 * The nine project statuses, in the order a project moves through them.
 * This is the only place their labels are written: filters, form selects,
 * the list column and the milestone bar all read from here.
 */
export const PROJECT_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  WAITING_REQUIREMENTS: 'Waiting Requirements',
  VALIDATING_REQUIREMENTS: 'Validating Requirements',
  IN_PROGRESS: 'In Progress',
  REPORT_SENT: 'Report Sent',
  RETESTING: 'Retesting',
  VALIDATION_REPORT_SENT: 'Validation Report Sent',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export interface ProjectStatusOption {
  value: string;
  label: string;
}

/** Every status, for filters — a project can be found in any of them. */
export const PROJECT_STATUS_OPTIONS: ProjectStatusOption[] = Object.entries(
  PROJECT_STATUS_LABELS,
).map(([value, label]) => ({ value, label }));

/**
 * Statuses a person may pick by hand — the same set `deriveProjectStatus`
 * refuses to overwrite, so a form can never offer a status the system would
 * immediately take back.
 */
export const PROJECT_STATUS_MANUAL_OPTIONS: ProjectStatusOption[] =
  PROJECT_STATUS_OPTIONS.filter((o) =>
    (PROJECT_STATUS_MANUAL_HOLD as readonly string[]).includes(o.value),
  );

/** The rest: the system assigns these as the project advances. */
export const PROJECT_STATUS_AUTOMATIC: string[] = PROJECT_STATUS_OPTIONS.filter(
  (o) => !(PROJECT_STATUS_MANUAL_HOLD as readonly string[]).includes(o.value),
).map((o) => o.value);

/**
 * Labels for the client milestone bar. Every step but the first is named after
 * the status it stands for; the first one also needs a word for "the client
 * sent them and we accepted them", which is not a status of its own.
 */
export const MILESTONE_LABELS: Record<string, string> = {
  ...PROJECT_STATUS_LABELS,
  REQUIREMENTS_VALIDATED: 'Requirements Validated',
};

export interface StatusStyle {
  bg: string;
  color: string;
  border: string;
}

/**
 * One colour per status, used by both portals so the same state never looks
 * like two different things. The hues are the ones the client project page
 * already used for its milestone pills — blue for work under way, green for
 * delivered, amber for a retest, purple for the validation report — so nobody
 * has to relearn them.
 */
export const PROJECT_STATUS_STYLES: Record<string, StatusStyle> = {
  DRAFT:                   { bg: 'rgba(161,161,170,0.12)', color: 'rgb(161,161,170)', border: 'rgba(161,161,170,0.3)' },
  WAITING_REQUIREMENTS:    { bg: 'rgba(234,179,8,0.10)',   color: 'rgb(202,138,4)',   border: 'rgba(234,179,8,0.3)' },
  VALIDATING_REQUIREMENTS: { bg: 'rgba(59,130,246,0.10)',  color: 'rgb(59,130,246)',  border: 'rgba(59,130,246,0.3)' },
  IN_PROGRESS:             { bg: 'rgba(3,78,252,0.10)',    color: 'rgb(3,78,252)',    border: 'rgba(3,78,252,0.3)' },
  REPORT_SENT:             { bg: 'rgba(16,185,129,0.10)',  color: 'rgb(16,185,129)',  border: 'rgba(16,185,129,0.3)' },
  RETESTING:               { bg: 'rgba(245,158,11,0.10)',  color: 'rgb(245,158,11)',  border: 'rgba(245,158,11,0.3)' },
  VALIDATION_REPORT_SENT:  { bg: 'rgba(139,92,246,0.10)',  color: 'rgb(139,92,246)',  border: 'rgba(139,92,246,0.3)' },
  COMPLETED:               { bg: 'rgba(52,211,153,0.12)',  color: 'rgb(16,163,127)',  border: 'rgba(52,211,153,0.35)' },
  CANCELLED:               { bg: 'rgba(239,68,68,0.10)',   color: 'rgb(220,38,38)',   border: 'rgba(239,68,68,0.3)' },
};

const NEUTRAL_STATUS_STYLE: StatusStyle = {
  bg: 'rgba(161,161,170,0.12)',
  color: 'rgb(161,161,170)',
  border: 'rgba(161,161,170,0.3)',
};

export function projectStatusStyle(status?: string | null): StatusStyle {
  return PROJECT_STATUS_STYLES[status ?? ''] ?? NEUTRAL_STATUS_STYLE;
}

export const SPECIALIST_HIERARCHY_LABELS: Record<string, string> = {
  LEAD: 'Lead',
  SENIOR: 'Senior',
  MID: 'Mid',
  JUNIOR: 'Junior',
};

export const VULNERABILITY_SEVERITY_LABELS: Record<string, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  INFORMATIONAL: 'Informational',
};

export const VULNERABILITY_REVIEW_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  PENDING_REVIEW: 'Under Review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const RETEST_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const ADMIN_ROLE_LABELS: Record<string, string> = {
  ADMINISTRATOR: 'Administrator',
  PENTESTER: 'Delta Hat',
};

export function formatAssetType(value?: string | null): string {
  if (!value) return '';
  const key = value.toUpperCase();
  return ASSET_TYPE_LABELS[key] || value;
}

export function formatVulnerabilityStatus(value?: string | null): string {
  if (!value) return '';
  const key = value.toUpperCase();
  return VULNERABILITY_STATUS_LABELS[key] || value;
}

export function formatAssessmentType(value?: string | null): string {
  if (!value) return '';
  const key = value.toUpperCase();
  return ASSESSMENT_TYPE_LABELS[key] || value;
}

export function formatProjectStatus(value?: string | null): string {
  if (!value) return '';
  const key = value.toUpperCase();
  return PROJECT_STATUS_LABELS[key] || value;
}

export function formatSpecialistHierarchy(value?: string | null): string {
  if (!value) return '';
  const key = value.toUpperCase();
  return SPECIALIST_HIERARCHY_LABELS[key] || value;
}

export function formatSeverity(value?: string | null): string {
  if (!value) return '';
  const key = value.toUpperCase();
  return VULNERABILITY_SEVERITY_LABELS[key] || value;
}

export function formatReviewStatus(value?: string | null): string {
  if (!value) return '';
  const key = value.toUpperCase();
  return VULNERABILITY_REVIEW_STATUS_LABELS[key] || value;
}

export function formatRetestStatus(value?: string | null): string {
  if (!value) return '';
  const key = value.toUpperCase();
  return RETEST_STATUS_LABELS[key] || value;
}

export function formatAdminRole(value?: string | null): string {
  if (!value) return '';
  const key = value.toUpperCase();
  return ADMIN_ROLE_LABELS[key] || value;
}

export function formatEnumLabel(
  value?: string | null,
  type?:
    | 'assetType'
    | 'vulnerabilityStatus'
    | 'assessmentType'
    | 'projectStatus'
    | 'hierarchy'
    | 'severity'
    | 'reviewStatus'
    | 'retestStatus'
    | 'role'
): string {
  if (!value) return '';
  if (type === 'assetType') return formatAssetType(value);
  if (type === 'vulnerabilityStatus') return formatVulnerabilityStatus(value);
  if (type === 'assessmentType') return formatAssessmentType(value);
  if (type === 'projectStatus') return formatProjectStatus(value);
  if (type === 'hierarchy') return formatSpecialistHierarchy(value);
  if (type === 'severity') return formatSeverity(value);
  if (type === 'reviewStatus') return formatReviewStatus(value);
  if (type === 'retestStatus') return formatRetestStatus(value);
  if (type === 'role') return formatAdminRole(value);

  const key = value.toUpperCase();
  return (
    ASSET_TYPE_LABELS[key] ||
    VULNERABILITY_STATUS_LABELS[key] ||
    ASSESSMENT_TYPE_LABELS[key] ||
    PROJECT_STATUS_LABELS[key] ||
    SPECIALIST_HIERARCHY_LABELS[key] ||
    VULNERABILITY_SEVERITY_LABELS[key] ||
    VULNERABILITY_REVIEW_STATUS_LABELS[key] ||
    RETEST_STATUS_LABELS[key] ||
    ADMIN_ROLE_LABELS[key] ||
    value.replace(/_/g, ' ')
  );
}

export const OWASP_WEB_TOP_10_2025 = [
  'Broken Access Control',
  'Security Misconfiguration',
  'Software Supply Chain Failures',
  'Cryptographic Failures',
  'Injection',
  'Insecure Design',
  'Authentication Failures',
  'Software or Data Integrity Failures',
  'Security Logging and Alerting Failures',
  'Mishandling of Exceptional Conditions',
];

export const OWASP_MOBILE_TOP_10_2024 = [
  'Improper Credential Usage',
  'Inadequate Supply Chain Security',
  'Insecure Authentication/Authorization',
  'Insufficient Input/Output Validation',
  'Insecure Communication',
  'Inadequate Privacy Controls',
  'Insufficient Binary Protections',
  'Security Misconfiguration',
  'Insecure Data Storage',
  'Insufficient Cryptography',
];

export const OWASP_API_TOP_10_2023 = [
  'Broken Object Level Authorization (BOLA)',
  'Broken Authentication',
  'Broken Object Property Level Authorization',
  'Unrestricted Resource Consumption',
  'Broken Function Level Authorization',
  'Unrestricted Access to Sensitive Business Flows',
  'Server Side Request Forgery (SSRF)',
  'Security Misconfiguration',
  'Improper Inventory Management',
  'Unsafe Consumption of APIs',
];

export function getOwaspCategoriesForAssetType(assetType?: string | null): string[] {
  if (!assetType) return [];
  switch (assetType.toUpperCase()) {
    case 'WEB':
      return OWASP_WEB_TOP_10_2025;
    case 'MOBILE':
      return OWASP_MOBILE_TOP_10_2024;
    case 'API':
      return OWASP_API_TOP_10_2023;
    default:
      return [];
  }
}

